/* eslint-disable @typescript-eslint/no-explicit-any */

import { DataLayerObject } from "@gtm-support/core";
import { EngagementProvider } from "@Base/Engagement/Engagement";
import { CookieManager } from "@Utilities/CookieManager";
import { CouponManager } from "@Utilities/CouponManager";
import { DataPrivacyPolicy } from "@Utilities/DataPrivacyPolicy";
import { ExperimentManager } from "@Utilities/ExperimentManager";
import { LocalRecommendation } from "@Base/Engagement/LocalRecommendation";
import { MalachyteRecommendation } from "@Base/Engagement/MalachyteRecommendation";
import { Media } from "@Utilities/Media";
import { Recommendations } from "@Base/Engagement/Recommendations";
import { Sezzle } from "@ThirdParty/Sezzle";
import { SubscriptionBase } from "@Base/Engagement/SubscriptionBase";
import { TagManager } from '@ThirdParty/TagManager';
import { Tracking } from "@Utilities/Tracking";
import { TextResource, TextResourceClass } from "@Utilities/TextResource";

declare global
{
	interface Window
	{
		attentive?: any;
		Carousel?: any;
		dataLayer?: DataLayerObject[];
		Fancybox?: any;
		Panzoom?: any;
		Fun: {
			Analytics: {
				AddTest?(thirdPartyTestID: string, variantSequence: number, callback?: Function);
			};
			CookieManager: CookieManager;
			Client: {
				ApplyCoupon?(couponCode: string);
				DisplayCouponDetails?(banner: HTMLElement);
				RemoveCoupon?();
			};
			DataPrivacyPolicy: DataPrivacyPolicy;
			ExperimentManager: ExperimentManager;
			Global:
			{
				AttentiveComplete?(data: any);
				AttentiveFixOverlay?();
				CreateGuestAccount?(email: string, phone: string, attentiveeEmail: boolean, attentiveSms: boolean);
				AddSubscription?(phone: string, email: string, subscriptionId: string);
				SubmitSubscriberForm?(form: any, showBanner: boolean, event?: Event);
				UpdateFooter?();
				GetRecommendations?(request: any);
				RenderLocalRecentlyViewed?(containerType: string, divName: string, title: string);
				RenderLocalRecommendations?(containerType: string, divName: string, title: string, elementName: string);
			};
			Media: Media;
			TagManager: TagManager;
			Theme: {
				Media?: string;
			};
			ThirdParty: {
				Sezzle: Sezzle;
			};
			Tracking: Tracking;
			Recommendations: Recommendations;
		};
		ContentRendered: boolean;
		EngagementProvider: EngagementProvider;
		TrackJSSettings: {
			token: string;
			application: string;
		};
		textResource: TextResourceClass;
		trackJs: any;
	}
}

export class Window
{
	private readonly couponManager: CouponManager;
	private readonly engagementProvider: EngagementProvider;
	private readonly experimentManager: ExperimentManager;
	private readonly localRecommendation: LocalRecommendation;
	private readonly malachyteRecommendation: MalachyteRecommendation;
	private readonly subscription: SubscriptionBase;
	private readonly tagManager: TagManager;

	/**
	 * Constructs the window object for exposing global functions
	 */
	constructor()
	{
		window.Carousel;
		window.Fancybox;
		window.Panzoom;

		this.couponManager = new CouponManager();
		this.engagementProvider = new EngagementProvider();
		this.experimentManager = new ExperimentManager();
		this.localRecommendation = new LocalRecommendation();
		this.malachyteRecommendation = new MalachyteRecommendation();
		this.subscription = new SubscriptionBase();

		window.Fun.Analytics =
		{
			AddTest: this.experimentManager.AddTest
		};

		window.Fun.CookieManager = CookieManager;

		window.Fun.Client =
		{
			ApplyCoupon: this.couponManager.ApplyCoupon,
			DisplayCouponDetails: this.couponManager.DisplayCouponDetails,
			RemoveCoupon: this.couponManager.RemoveCoupon
		};

		window.Fun.DataPrivacyPolicy = new DataPrivacyPolicy();
		window.Fun.ExperimentManager = this.experimentManager;

		window.Fun.Global =
		{
			AttentiveComplete: this.engagementProvider.SmsCallback,
			AttentiveFixOverlay: this.engagementProvider.FixOverlay,
			CreateGuestAccount: this.engagementProvider.CreateGuestAccount,
			AddSubscription: this.engagementProvider.AddSubscription,
			SubmitSubscriberForm: this.subscription.Submit,
			UpdateFooter: this.subscription.UpdateFooter,
			GetRecommendations: this.malachyteRecommendation.GetRecommendations,
			RenderLocalRecentlyViewed: this.localRecommendation.RenderRecentlyViewed,
			RenderLocalRecommendations: this.localRecommendation.RenderRecommendations
		};

		window.Fun.Media = Media;

		window.Fun.Recommendations = new Recommendations();

		window.Fun.Theme = {
			Media: Media.DeviceType
		};

		window.Fun.ThirdParty = {
			Sezzle: new Sezzle()
		};

		window.Fun.Tracking = new Tracking();

		window.ContentRendered = false;
		window.EngagementProvider = this.engagementProvider;
		window.textResource = TextResource;
	}
}